import { z } from 'zod';
// ============================================================
// 2-4: 在り方（Arikata）Enum — 付喪神の5つの在り方
// ============================================================
export const ArikataEnum = z.enum([
    'najimi', // 馴染み — 人間と共にある共存派
    'uraichi', // 裏市   — 独自経済圏を築く自由自治区
    'tatari', // 祟り   — 人間に敵意を持つ危険な存在
    'hagure', // はぐれ — どこにも属さない個別主義者
    'madoromi', // まどろみ — まだ完全に目覚めていない半覚醒
]);
/** 日本語表示名のマッピング */
export const ARIKATA_LABELS = {
    najimi: '馴染み',
    uraichi: '裏市',
    tatari: '祟り',
    hagure: 'はぐれ',
    madoromi: 'まどろみ',
};
// ============================================================
// 2-3: 等級スキーマ — 危険度分類 1a〜5c
// ============================================================
export const GradeLevelEnum = z.enum(['1', '2', '3', '4', '5']);
export const GradeSubEnum = z.enum(['a', 'b', 'c']);
export const GradeSchema = z.object({
    level: GradeLevelEnum,
    sub: GradeSubEnum,
});
/** 等級の説明 */
export const GRADE_DESCRIPTIONS = {
    '1': '低危険度。日常に紛れても害がほぼない',
    '2': 'やや危険。小規模な騒動を起こしうる',
    '3': '中危険度。人間に明確な影響を与えうる',
    '4': '高危険度。広範囲に被害を及ぼしうる',
    '5': '最高危険度。存在自体が脅威',
};
/** 等級を文字列表現に変換（例: "2b"） */
export function gradeToString(grade) {
    return `${grade.level}${grade.sub}`;
}
/** 文字列から等級をパース（例: "2b" → { level: '2', sub: 'b' }） */
export function parseGrade(s) {
    const match = s.match(/^([1-5])([abc])$/);
    if (!match)
        return null;
    return { level: match[1], sub: match[2] };
}
// ============================================================
// 2-5: 禁止能力カテゴリ — 自動生成で絶対に除外する6カテゴリ
// ============================================================
export const ForbiddenAbilityCategoryEnum = z.enum([
    'time_space', // 時空操作 — 前作の時空アーティファクトと競合
    'reincarnation', // 転生 — 前作の転生のアーティファクト（マッチ）と競合
    'wish_granting', // 願望実現 — 前作の阿部幸雄融合体と競合
    'contract_force', // 契約強制 — 前作の契約のアーティファクトと競合
    'ego_granting', // 自我付与 — 壺のアーティファクト / ヤドリギと競合
    'mind_control', // 支配・洗脳 — レイトの能力と競合
]);
/** 禁止能力の日本語ラベルと競合理由 */
export const FORBIDDEN_ABILITY_INFO = {
    time_space: { label: '時空操作', reason: '前作の時空アーティファクトと競合' },
    reincarnation: { label: '転生', reason: '前作の転生のアーティファクト（マッチ）と競合' },
    wish_granting: { label: '願望実現', reason: '前作の阿部幸雄融合体と競合' },
    contract_force: { label: '契約強制', reason: '前作の契約のアーティファクトと競合' },
    ego_granting: { label: '自我付与', reason: '壺のアーティファクト / ヤドリギと競合' },
    mind_control: { label: '支配・洗脳', reason: 'レイトの能力と競合' },
};
// ============================================================
// 覚醒パターン（自我の獲得方法）
// ============================================================
export const AwakenTypeEnum = z.enum([
    'natural', // 自然覚醒 — 物自体が長い年月をかけて自力で目覚める
    'promoted', // 促進覚醒 — 壺のアーティファクトにより安全に覚醒を促す
    'forced', // 強制覚醒 — 外部から無理やり自我を目覚めさせる（デメリット大）
]);
// ============================================================
// 推奨トーン
// ============================================================
export const ToneEnum = z.enum([
    'heartwarming', // ほっこり
    'serious', // シリアス
    'horror', // ホラー
    'comedy', // コメディ
    'suspense', // サスペンス
    'action', // アクション
    'mystery', // ミステリー
]);
// ============================================================
// 解決後の結末パターン
// ============================================================
export const AftermathEnum = z.enum([
    'stay', // 留まる（馴染みに加わる等）
    'leave', // 去る（自分の道を行く）
    'sealed', // 封じられる
    'purified', // 浄化される（祟り→穏やかな状態に）
    'other', // その他
]);
// ============================================================
// 2-2: TsukumogamiProfile — 付喪神プロファイル（5セクション）
// ============================================================
/** 基本情報 */
export const TsukumogamiBasicSchema = z.object({
    name: z.string().min(1).describe('自我を持った後の自称'),
    originalObject: z.string().min(1).describe('元の物'),
    appearance: z.string().describe('人型を取る場合の外見描写'),
    grade: GradeSchema,
    arikata: ArikataEnum,
    awakenType: AwakenTypeEnum.optional().describe('覚醒パターン'),
});
/** 能力 */
export const TsukumogamiAbilitySchema = z.object({
    primary: z.string().min(1).describe('主能力（元の物に紐づく超常能力）'),
    cost: z.string().describe('代償（能力の代価・制限）'),
    traits: z.array(z.string()).describe('特性（能力の副次的な性質）'),
    weakness: z.string().describe('弱点（対処法のヒント）'),
});
/** 人格 */
export const TsukumogamiPersonalitySchema = z.object({
    traits: z.array(z.string()).min(1).describe('性格特性'),
    speechStyle: z.string().describe('口調・話し方'),
    desire: z.string().describe('願望（何を求めているか）'),
    fear: z.string().describe('恐れ（何を避けたいか）'),
});
/** 事件 */
export const TsukumogamiIncidentSchema = z.object({
    summary: z.string().min(1).describe('引き起こす問題の概要'),
    scale: z.string().describe('被害の規模・影響範囲'),
    clue: z.string().describe('解決の糸口'),
    aftermath: AftermathEnum.describe('解決後の付喪神の反応'),
    aftermathDetail: z.string().optional().describe('解決後の詳細描写'),
});
/** メタ情報 */
export const TsukumogamiMetaSchema = z.object({
    conflictCheck: z.object({
        passed: z.boolean(),
        conflicts: z.array(z.string()).default([]),
        checkedCategories: z.array(ForbiddenAbilityCategoryEnum).default([]),
    }).optional().describe('既存要素との競合チェック結果'),
    estimatedPlayTimeMin: z.number().min(30).max(120).describe('推定プレイ時間（分）の下限'),
    estimatedPlayTimeMax: z.number().min(30).max(120).describe('推定プレイ時間（分）の上限'),
    difficulty: z.enum(['easy', 'normal', 'hard']).describe('難易度目安'),
    tone: ToneEnum.describe('推奨トーン'),
    collabAffinity: z.string().optional().describe('コラボ適性（あれば）'),
});
/** TsukumogamiProfile 本体 */
export const TsukumogamiProfileSchema = z.object({
    id: z.string().min(1).describe('一意識別子'),
    basic: TsukumogamiBasicSchema,
    ability: TsukumogamiAbilitySchema,
    personality: TsukumogamiPersonalitySchema,
    incident: TsukumogamiIncidentSchema,
    meta: TsukumogamiMetaSchema,
});
// ============================================================
// 既存キャラとの競合チェック用リスト
// ============================================================
/** 九十九商会の常駐メンバーが占有する元の物（重複禁止） */
export const RESERVED_ORIGINAL_OBJECTS = [
    '図書館', // はじめ
    '鏡', // カガミ
    '筆', // フデ
    '盆栽', // ヤドリギ
    '狐のお面', // コメン
    '指輪', // リキッド
];
/** 予約済み能力キーワード（競合チェック用） */
export const RESERVED_ABILITY_KEYWORDS = [
    '時空', '時間操作', 'タイムトラベル',
    '転生', '生まれ変わり',
    '願望実現', '願い',
    '契約強制', '契約',
    '自我付与', '意思を与える',
    '支配', '洗脳', 'マインドコントロール',
];
// ============================================================
// 2-6: stepSchema との統合設計
// ============================================================
// TsukumogamiProfile → ScenarioContent への接続。
// シナリオ生成時、付喪神プロファイルをシナリオのメタデータとして保持し、
// 各ステップ生成の入力パラメータとして参照する。
/** シナリオに紐づく付喪神の参照情報 */
export const ScenarioTsukumogamiRefSchema = z.object({
    /** 付喪神プロファイルID */
    profileId: z.string().min(1),
    /** 役割: 主役（事件を起こす）/ 協力者 / 背景（世界観のみ） */
    role: z.enum(['protagonist', 'ally', 'background']),
});
/** ScenarioContent に追加する付喪神メタデータ */
export const ScenarioTsukumogamiMetaSchema = z.object({
    /** このシナリオに登場する付喪神たち */
    tsukumogami: z.array(ScenarioTsukumogamiRefSchema).default([]),
    /** インラインで埋め込まれた完全なプロファイル（自動生成時に使用） */
    profiles: z.array(TsukumogamiProfileSchema).optional(),
});
/**
 * stepSchema への接続設計:
 *
 * DialogStep:
 *   - personality.speechStyle → lines[].text の生成時に口調を反映
 *   - basic.name → speaker名として使用
 *   - personality.traits → 選択肢に対する反応パターンの生成
 *
 * SceneStep:
 *   - incident.summary → 背景・演出の選択に影響
 *   - meta.tone → BGM・背景の雰囲気選択
 *
 * MinigameStep:
 *   - ability → ミニゲームの題材（下記 2-7 参照）
 *   - basic.grade → difficulty の自動決定
 *
 * BranchStep:
 *   - incident.clue → 正解ルートの分岐条件生成
 *   - incident.aftermath → エンディング分岐への入力
 *
 * EndingStep:
 *   - incident.aftermath → endingType の決定
 *   - personality.desire/fear → エンディングテキストの生成
 */
// ============================================================
// 2-7: minigameConfig との統合設計
// ============================================================
// 付喪神の等級・能力から適切なミニゲームテンプレートを推薦する。
/** 等級レベルから推奨されるミニゲーム難易度へのマッピング */
export const GRADE_TO_DIFFICULTY = {
    '1': 'easy',
    '2': 'easy',
    '3': 'normal',
    '4': 'hard',
    '5': 'hard',
};
/** 在り方ごとの推奨ミニゲームテンプレートID */
export const ARIKATA_TO_MINIGAME_TEMPLATES = {
    najimi: [1, 6, 10], // Investigation, Comparison, InvestigationV2 — 日常に紛れた調査系
    uraichi: [4, 5, 7, 11], // Timeline, EvidenceBoard, Contradiction, ContradictionV2 — 取引・交渉・推理系
    tatari: [8, 9, 16, 18], // Chase, IntenseChase, ExistenceErosion, HorrorQTE — アクション・ホラー系
    hagure: [1, 3, 5, 7], // Investigation, Cipher, EvidenceBoard, Contradiction — 汎用的
    madoromi: [2, 3, 15], // Fingerprint, Cipher, DoorSelection — 覚醒の手がかり探索系
};
/** トーンごとの推奨ミニゲームテンプレートID */
export const TONE_TO_MINIGAME_TEMPLATES = {
    heartwarming: [1, 6, 10], // 穏やかな調査系
    serious: [5, 7, 11, 4], // 証拠・矛盾追及系
    horror: [9, 16, 17, 18], // 追跡・侵食・ホラー系
    comedy: [1, 3, 6, 15], // 軽めのパズル・調査系
    suspense: [7, 8, 11, 17], // 矛盾・追跡・タイミング系
    action: [8, 9, 17, 18], // チェイス・QTE系
    mystery: [2, 3, 4, 5], // 指紋・暗号・タイムライン・証拠ボード系
};
//# sourceMappingURL=tsukumogamiSchema.js.map